import type { CryptoWithdrawal, WithdrawalStatus } from '../@types/api';
import { type ApprovalActionConfig, getActionsForStatus } from '../constants/approvalActions';

/** Normalized withdrawal provider identity; anything unrecognised or contradictory is `invalid`. */
export type WithdrawalProvider = 'legacy' | 'zero_x' | 'invalid';

type WithdrawalRow = {
  status: WithdrawalStatus;
  paymentMethod?: string;
  paymentProvider?: string | null;
  cryptoWithdrawal?: CryptoWithdrawal;
};

const LEGACY_PROVIDERS = new Set(['LIGDICASH', 'PAY2LOCAL', 'KAPOPAY', 'CRYPTO']);
const ZERO_X_PROVIDER = 'ZERO_X_PROCESSING';

export const normalizeWithdrawalProvider = ({
  paymentMethod,
  paymentProvider,
  cryptoWithdrawal,
}: WithdrawalRow): WithdrawalProvider => {
  const provider = paymentProvider?.trim().toUpperCase() || null;
  const zeroXFacts = cryptoWithdrawal?.provider === 'zero_x_processing';

  if (provider === ZERO_X_PROVIDER) return zeroXFacts ? 'zero_x' : 'invalid';
  if (cryptoWithdrawal) return 'invalid';
  // Missing provider facts must not turn a submitted crypto payout into a manual withdrawal.
  if (provider === null && paymentMethod?.trim().toUpperCase() === 'CRYPTO') return 'invalid';
  if (provider === null || LEGACY_PROVIDERS.has(provider)) return 'legacy';
  return 'invalid';
};

/** The complete `(provider, status, provider state) → actions` decision. Components only render it. */
export const getWithdrawalActions = (row: WithdrawalRow): ApprovalActionConfig[] => {
  const provider = normalizeWithdrawalProvider(row);

  switch (provider) {
    case 'legacy':
      return getActionsForStatus(row.status);
    case 'zero_x':
      // Once submitted, only verified provider evidence may settle or release a 0x payout.
      return row.status === 'pending' && row.cryptoWithdrawal?.state === 'awaiting_approval'
        ? getActionsForStatus('pending')
        : [];
    case 'invalid':
      return [];
  }
};
